/**
 * Formate les détails de calcul en HTML. Nécessite t() pour l'i18n.
 * @param {object} calcDetails - Détails bruts depuis l'API
 * @param {function} t - Fonction de traduction (key, opts?) => string
 * @return {string} Chaîne HTML
 */
export function formatCalculationDetails(calcDetails, t) {
  if (!calcDetails) {
    return `<p>${t("NoDetailsAvailable")}</p>`;
  }
  const bi = calcDetails.correctedCriteria?.businessInitiative || calcDetails.inputCriteria?.businessInitiative;
  const ee = calcDetails.correctedCriteria?.experimentalEnvironment || calcDetails.inputCriteria?.experimentalEnvironment;
  const finalScore = calcDetails.intermediateCalculations?.finalScore;
  const formatStrategicResponsibility = value => {
    if (value === undefined || value === null) return t("CalcNotDefined");
    const keys = {
      0: "CalcNoMention",
      1: "CalcConsulted",
      2: "CalcResponsibleExecutes",
      3: "CalcAccountableDecides"
    };
    return keys[value] ? t(keys[value]) : t("CalcUnknownValue", {
      value
    });
  };
  const formatProductMarketingActivities = value => {
    if (value === undefined || value === null) return t("CalcNotDefined");
    const keys = {
      0: "CalcNoMention",
      1: "CalcCollaboratesWithProductMarketer",
      2: "CalcAccountableWithProductMarketer"
    };
    return keys[value] ? t(keys[value]) : t("CalcUnknownValue", {
      value
    });
  };
  const formatGrowthActivities = value => {
    if (value === undefined || value === null) return t("CalcNotDefined");
    const keys = {
      0: "CalcNoMention",
      1: "CalcCollaboratesWithGrowthManager",
      2: "CalcAccountableWithGrowthManager"
    };
    return keys[value] ? t(keys[value]) : t("CalcUnknownValue", {
      value
    });
  };
  const formatUXActivities = value => {
    if (value === undefined || value === null) return t("CalcNotDefined");
    const keys = {
      0: "CalcNoMention",
      1: "CalcCollaboratesWithUXDesigner"
    };
    return keys[value] ? t(keys[value]) : t("CalcUnknownValue", {
      value: String(value)
    });
  };
  const formatDeliveryActivities = value => {
    if (value === undefined || value === null) return t("CalcNotDefined");
    const keys = {
      0: "CalcNoMention",
      1: "CalcAccountableNotExecutes",
      2: "CalcResponsibleNotDecides",
      3: "CalcAccountableAndResponsible"
    };
    return keys[value] ? t(keys[value]) : t("CalcUnknownValue", {
      value: String(value)
    });
  };
  const smellKeyToI18n = {
    deliveryListedBeforeTacticsAndStrategy: "CalcSmellDeliveryListedFirst",
    featureTeam: "CalcSmellFeatureTeam",
    titleIsProductOwner: "CalcSmellTitleProductOwner",
    productManagerWorksWithDistinctProductOwner: "CalcSmellPMWithDistinctPO",
    usesSAFe: "CalcSmellUsesSAFe",
    collaboratesWithDistinctBusinessTeam: "CalcSmellDistinctBusinessTeam",
    userNeedsOverBusinessValue: "CalcSmellUserNeedsOverValue",
    manageMultipleProducts: "CalcSmellManageMultipleProducts",
    noDistinctUXDesigner: "CalcSmellNoDistinctUXDesigner",
    uxOptimizationOverExperimentation: "CalcSmellUXOptimization",
    productMarketingOptimizationOverExperimentation: "CalcSmellProductMarketingOptimization",
    growthMarketingOptimizationOverExperimentation: "CalcSmellGrowthOptimization",
    featureDeliverySpeedOverBusinessValueDelivery: "CalcSmellFeatureDeliverySpeed",
    internalProduct: "CalcSmellInternalProduct"
  };
  const criterionLabel = (labelKey, tooltipKey) => {
    const tip = t(tooltipKey).replace(/"/g, "&quot;");
    return `<span class="criterion-with-tooltip" title="${tip}">${t(labelKey)}</span>`;
  };
  const formatSmellName = key => {
    const i18nKey = smellKeyToI18n[key];
    return i18nKey ? t(i18nKey) : key;
  };
  let html = "<div class=\"calculation-details-container\">";
  html += "<div class=\"calculation-details-columns\">";
  html += "<div class=\"calculation-column calculation-column-left\">";
  html += `<h6>💼 ${t("BusinessInitiative")}</h6>`;
  if (bi) {
    const strategic = bi.strategicResponsibilities;
    if (strategic) {
      html += "<div class=\"criteria-group\">";
      html += `<strong>${t("CalcStrategicResponsibilities")}</strong>`;
      html += "<ul>";
      if (strategic.businessModelDefinition !== undefined && strategic.businessModelDefinition !== null) {
        html += `<li><strong>${criterionLabel("CalcBusinessModelDefinition", "CalcTooltipBusinessModelDefinition")}</strong> ${formatStrategicResponsibility(strategic.businessModelDefinition)}</li>`;
      }
      if (strategic.productStrategyDefinition !== undefined && strategic.productStrategyDefinition !== null) {
        html += `<li><strong>${criterionLabel("CalcProductStrategyDefinition", "CalcTooltipProductStrategyDefinition")}</strong> ${formatStrategicResponsibility(strategic.productStrategyDefinition)}</li>`;
      }
      html += "</ul></div>";
    }
    const tactical = bi.tacticalAccountabilities;
    if (tactical) {
      html += "<div class=\"criteria-group\">";
      html += `<strong>${t("CalcTacticalAccountabilities")}</strong>`;
      html += "<ul>";
      if (tactical.productMarketingActivities !== undefined && tactical.productMarketingActivities !== null) {
        html += `<li><strong>${criterionLabel("CalcProductMarketingActivities", "CalcTooltipProductMarketingActivities")}</strong> ${formatProductMarketingActivities(tactical.productMarketingActivities)}</li>`;
      }
      if (tactical.growthActivities !== undefined && tactical.growthActivities !== null) {
        html += `<li><strong>${criterionLabel("CalcGrowthActivities", "CalcTooltipGrowthActivities")}</strong> ${formatGrowthActivities(tactical.growthActivities)}</li>`;
      }
      if (tactical.userExperienceActivities !== undefined && tactical.userExperienceActivities !== null) {
        html += `<li><strong>${criterionLabel("CalcUXActivities", "CalcTooltipUXActivities")}</strong> ${formatUXActivities(tactical.userExperienceActivities)}</li>`;
      }
      if (tactical.deliveryActivities !== undefined && tactical.deliveryActivities !== null) {
        html += `<li><strong>${criterionLabel("CalcDeliveryActivities", "CalcTooltipDeliveryActivities")}</strong> ${formatDeliveryActivities(tactical.deliveryActivities)}</li>`;
      }
      html += "</ul></div>";
    }
    const biSmells = bi.smells || {};
    const activeBiSmells = Object.keys(biSmells).filter(key => biSmells[key] === true);
    html += "<div class=\"criteria-group\">";
    html += `<strong>⚠️ ${t("CalcNegativeIndicators")}</strong>`;
    if (activeBiSmells.length > 0) {
      html += "<ul>";
      activeBiSmells.forEach(smell => {
        html += `<li>${formatSmellName(smell)}</li>`;
      });
      html += "</ul>";
    } else {
      html += `<p class="no-indicators"><em>${t("CalcNone")}</em></p>`;
    }
    html += "</div>";
  } else {
    html += `<p><em>${t("CalcNoData")}</em></p>`;
  }
  html += "</div>";
  html += "<div class=\"calculation-column calculation-column-right\">";
  html += `<h6>🔬 ${t("ExperimentalEnvironment")}</h6>`;
  if (ee) {
    html += "<div class=\"criteria-group\">";
    html += `<strong>${t("CalcCustomerDiscoveryActivities")}</strong>`;
    html += "<ul>";
    html += `<li><strong>${t("CalcProductMarketingDiscovery")}</strong> ${ee.productMarketingCustomerDiscovery ? "✅ " + t("CalcMentionedPositioning") : "❌ " + t("CalcNotMentioned")}</li>`;
    html += `<li><strong>${t("CalcGrowthMarketingDiscovery")}</strong> ${ee.growthMarketingCustomerDiscovery ? "✅ " + t("CalcMentionedAcquisition") : "❌ " + t("CalcNotMentioned")}</li>`;
    html += `<li><strong>${t("CalcUXDiscovery")}</strong> ${ee.UXCustomerDiscovery ? "✅ " + t("CalcMentionedUX") : "❌ " + t("CalcNotMentioned")}</li>`;
    html += `<li><strong>${t("CalcContinuousDelivery")}</strong> ${ee.continuousDelivery ? "✅ " + t("CalcMentionedFrequent") : "❌ " + t("CalcNotMentioned")}</li>`;
    html += "</ul></div>";
    const eeSmells = ee.smells || {};
    const activeEESmells = Object.keys(eeSmells).filter(key => eeSmells[key] === true);
    html += "<div class=\"criteria-group\">";
    html += `<strong>⚠️ ${t("CalcNegativeIndicators")}</strong>`;
    if (activeEESmells.length > 0) {
      html += "<ul>";
      activeEESmells.forEach(smell => {
        html += `<li>${formatSmellName(smell)}</li>`;
      });
      html += "</ul>";
    } else {
      html += `<p class="no-indicators"><em>${t("CalcNone")}</em></p>`;
    }
    html += "</div>";
  } else {
    html += `<p><em>${t("CalcNoData")}</em></p>`;
  }
  html += "</div>";
  html += "</div>";
  if (finalScore) {
    html += "<div class=\"calculation-final-score\">";
    html += `<h6>📊 ${t("CalcFinalScore")}</h6>`;
    html += "<div class=\"final-score-content\">";
    html += `<div class="final-score-item"><strong>${t("BusinessInitiative")}:</strong> ${finalScore.businessInitiativeScore?.toFixed(2) ?? "N/A"}/5</div>`;
    html += `<div class="final-score-item"><strong>${t("ExperimentalEnvironment")}:</strong> ${finalScore.experimentalEnvironmentScore?.toFixed(2) ?? "N/A"}/5</div>`;
    html += `<div class="final-score-item"><strong>${t("CalcWeighting")}</strong> 60% / 40%</div>`;
    html += `<div class="final-score-total"><strong>${t("CalcNutriScoreScore")}</strong> <span class="final-score-value">${finalScore.calculatedScore?.toFixed(2) ?? "N/A"}/5</span></div>`;
    html += "</div></div>";
  }
  html += "</div>";
  return html;
}